// =============================================================================
// EntityDocumentsPanel — files governance documents (Operating Agreements,
// EIN letters, …) catalogued from the "Entity Docs" SharePoint folder against
// dbo.SageEntities. See api/src/functions/sageEntityDocuments.js.
//
// NOTHING IS EVER AUTO-FILED. A scan only ever ADDS unfiled rows with a
// best-effort suggestion attached (name/keyword matching, never AI) — filing
// one still takes an explicit person picking an entity and a document type
// and pressing File. The suggestion chips exist to save clicks on the
// obvious cases, not to remove the confirmation step.
//
// The SharePoint file itself is never touched here: "Open in SharePoint" is
// the stored web link, and "Remove" only deletes OUR catalogue row (the
// original stays exactly where it was — the ShareFile-ingest rule).
// =============================================================================
import { useState } from 'react';
import { B } from '../../constants/brand';
import { api } from '../../api/client';
import { useApi } from '../../hooks/useApi';
import { Btn, Spinner, ErrorMsg, Card, Badge, Select, EmptyState, useToast } from '../ui';
import { InlineSearchPicker } from '../tasks/TaskComponents';

const fmtBytes = (n) => {
  if (n == null) return null;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

// Split an Aliases field the way the API does (ourEntities.aliasList): one per
// line, and a comma only where what follows is more than a legal form — so
// "Forte Capital Group, LLC" stays one name.
const LEGAL_FORMS = new Set(['llc', 'lllp', 'llp', 'lp', 'plc', 'pllc', 'pc', 'inc', 'incorporated', 'corp',
  'corporation', 'co', 'company', 'ltd', 'limited']);
const FILLER = new Set([...LEGAL_FORMS, 'the', 'and', 'of', 'a', 'an']);
const normName = (s) => String(s || '').toLowerCase().replace(/[.,/#!$%^&*;:{}=\-_`~()'"]/g, ' ').replace(/\s+/g, ' ').trim();
const isLegalFormOnly = (s) => { const t = normName(s).split(' ').filter(Boolean); return t.length > 0 && t.every(x => LEGAL_FORMS.has(x)); };
export const splitAliasNames = (raw) => {
  const out = [];
  for (const line of String(raw || '').split(/\n/)) {
    for (const piece of line.split(',').map(p => p.trim()).filter(Boolean)) {
      if (out.length && isLegalFormOnly(piece) && !isLegalFormOnly(out[out.length - 1])) out[out.length - 1] = `${out[out.length - 1]}, ${piece}`;
      else out.push(piece);
    }
  }
  return out;
};
// The distinctive part of a name, for "is this already one of its names" only.
// The server decides what is actually saved (entityDocuments.newAliasesFor).
const coreName = (s) => normName(s).split(' ').filter(t => t.length > 1 && !FILLER.has(t)).join(' ');

const UnfiledRow = ({ doc, entities, entityItems, typeOptions, onFiled, onRemoved, onEntityChanged }) => {
  const { toast } = useToast();
  const [sageEntityId, setSageEntityId] = useState(doc.suggestedSageEntityID || '');
  const [docType, setDocType] = useState(doc.suggestedDocType || '');
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [saveNames, setSaveNames] = useState(true);

  // Names this document ties together ("f/k/a …") that the chosen entity is
  // not known by yet — offered so documents under any of its names match it.
  const chosen = (entities || []).find(e => e.sageEntityID === sageEntityId);
  const knownCores = new Set(chosen ? [chosen.name, ...splitAliasNames(chosen.aliases)].map(coreName) : []);
  const newNames = chosen ? (doc.associatedNames || []).filter(n => !knownCores.has(coreName(n))) : [];

  const file = async () => {
    if (!sageEntityId || !docType) return;
    setSaving(true);
    try {
      const addAliases = saveNames ? newNames : [];
      const saved = await api.put(`sage/entities/documents/${doc.documentID}`, { sageEntityId, docType, addAliases });
      if (saved.aliasesAdded?.length) {
        toast.success(`Filed — ${chosen?.name || 'the entity'} is now also known as ${saved.aliasesAdded.join(', ')}`);
      } else {
        toast.success('Filed');
      }
      if (saved.aliasError) toast.error(saved.aliasError);
      if (saved.entity) onEntityChanged?.(saved.entity, saved.suggestionsFilled > 0);
      onFiled(saved);
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Remove "${doc.fileName}" from this list? The file stays in SharePoint — this only removes our catalogue entry, and it will come back on the next scan if it is still there.`)) return;
    setRemoving(true);
    try {
      await api.del(`sage/entities/documents/${doc.documentID}`);
      onRemoved(doc.documentID);
    } catch (e) { toast.error(e.message); setRemoving(false); }
  };

  return (
    <div style={{ padding: '12px 14px', borderBottom: `1px solid ${B.border}` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 13, color: B.text }}>{doc.fileName}</div>
          <div style={{ fontSize: 11, color: B.textLt, marginTop: 2 }}>
            {[doc.sharePointPath, fmtBytes(doc.fileSizeBytes)].filter(Boolean).join(' · ')}
          </div>
          {doc.sharePointWebUrl && (
            <a href={doc.sharePointWebUrl} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: B.navy }}>
              Open in SharePoint ↗
            </a>
          )}
        </div>
        <Btn size="sm" variant="ghost" onClick={remove} loading={removing}>Remove</Btn>
      </div>

      {(doc.suggestedEntityName || doc.suggestedDocTypeLabel) && (
        <div style={{ fontSize: 11, color: B.textLt, margin: '8px 0 0' }}>
          Suggested — {[doc.suggestedEntityName, doc.suggestedDocTypeLabel].filter(Boolean).join(' · ')}. Confirm below.
        </div>
      )}

      {chosen && splitAliasNames(chosen.aliases).length > 0 && (
        <div style={{ fontSize: 11, color: B.textLt, marginTop: 4 }}>
          {chosen.name} is also known as {splitAliasNames(chosen.aliases).join(' · ')}
        </div>
      )}

      {newNames.length > 0 && (
        <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 12, color: B.text, marginTop: 8, cursor: 'pointer' }}>
          <input type="checkbox" checked={saveNames} onChange={e => setSaveNames(e.target.checked)} style={{ marginTop: 2 }} />
          <span>
            This document also calls the entity{' '}
            {newNames.map((n, i) => <span key={n}>{i > 0 ? ', ' : ''}<strong>{n}</strong></span>)}.
            {' '}Save {newNames.length === 1 ? 'it' : 'them'} as other names for {chosen.name}, so documents and closings
            {' '}under any of its names are matched to it.
          </span>
        </label>
      )}

      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', flexWrap: 'wrap', marginTop: 8 }}>
        <div style={{ minWidth: 220, flex: '1 1 220px' }}>
          <InlineSearchPicker label="Entity" items={entityItems} value={sageEntityId} onChange={setSageEntityId}
            placeholder="Search entities…" clearLabel="— Choose an entity —" />
        </div>
        <div style={{ minWidth: 220, flex: '1 1 220px' }}>
          <Select label="Document type" value={docType} onChange={setDocType} options={typeOptions} />
        </div>
        <Btn size="sm" variant="primary" onClick={file} loading={saving} disabled={!sageEntityId || !docType} style={{ marginTop: 22 }}>
          File
        </Btn>
      </div>
    </div>
  );
};

// The org chart only ever reads a CURRENT filing's ownership (see
// sageEntityDocuments.js's getEntityOrgChart) — so the read/re-read
// affordance is offered on every filed document (an admin may want to check
// what a past or draft version says) but the sentence makes clear which
// state actually feeds the chart.
const OWNERSHIP_LABEL = {
  ready: { text: 'Ownership read', color: 'tealDk' },
  unknown: { text: 'No ownership breakdown found', color: 'amber' },
  error: { text: "Couldn't read this document", color: 'red' },
};

const OwnershipStatus = ({ doc, onUpdated }) => {
  const { toast } = useToast();
  const [reading, setReading] = useState(false);

  const read = async () => {
    setReading(true);
    try {
      const res = await api.post(`sage/entities/documents/${doc.documentID}/extract-ownership`, {});
      if (!res.available) {
        toast.error(res.reason || 'Could not read this document.');
      } else {
        if (res.document) onUpdated(res.document);
        if (res.status === 'ready') toast.success(`Found ${res.members} member${res.members === 1 ? '' : 's'}.`);
        else if (res.status === 'unknown') toast.info('No ownership breakdown found in this document.');
        else toast.error(res.reason || "Couldn't read this document.");
      }
    } catch (e) { toast.error(e.message); }
    finally { setReading(false); }
  };

  const info = OWNERSHIP_LABEL[doc.ownershipStatus];
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4, flexWrap: 'wrap' }}>
      {info ? (
        <Badge color={B[info.color]}>{info.text}</Badge>
      ) : (
        <span style={{ fontSize: 11, color: B.textLt }}>Ownership not read yet</span>
      )}
      {doc.ownershipStatus === 'ready' && doc.ownershipAsOfDate && (
        <span style={{ fontSize: 11, color: B.textLt }}>as of {doc.ownershipAsOfDate}</span>
      )}
      <Btn size="sm" variant="ghost" onClick={read} loading={reading}>
        {doc.ownershipStatus ? 'Re-read' : 'Read ownership'}
      </Btn>
    </div>
  );
};

const FiledRow = ({ doc, onRemoved, onOwnershipUpdated }) => {
  const { toast } = useToast();
  const [removing, setRemoving] = useState(false);

  const remove = async () => {
    if (!window.confirm(`Remove "${doc.fileName}" from this list? The file stays in SharePoint — only our catalogue entry (and its filing) is removed.`)) return;
    setRemoving(true);
    try {
      await api.del(`sage/entities/documents/${doc.documentID}`);
      onRemoved(doc.documentID);
    } catch (e) { toast.error(e.message); setRemoving(false); }
  };

  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, padding: '8px 14px', borderBottom: `1px solid ${B.border}` }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <Badge color={B.tealDk}>{doc.docTypeLabel}</Badge>
          <span style={{ fontSize: 13, color: B.text }}>{doc.fileName}</span>
        </div>
        {doc.sharePointWebUrl && (
          <a href={doc.sharePointWebUrl} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: B.navy }}>
            Open in SharePoint ↗
          </a>
        )}
        {doc.docType === 'OA_CURRENT' && <OwnershipStatus doc={doc} onUpdated={onOwnershipUpdated} />}
      </div>
      <Btn size="sm" variant="ghost" onClick={remove} loading={removing}>Remove</Btn>
    </div>
  );
};

export const EntityDocumentsPanel = ({ entities, onEntityChanged }) => {
  const { toast } = useToast();
  const { data, loading, error, reload } = useApi(() => api.get('sage/entities/documents'), []);
  const [scanning, setScanning] = useState(false);
  // Applied on top of the fetched list so a file/remove renders instantly
  // without a full reload — useApi has no setter.
  const [removed, setRemoved] = useState(new Set());
  const [refiled, setRefiled] = useState({});

  const scan = async () => {
    setScanning(true);
    try {
      const res = await api.post('sage/entities/documents/scan', {});
      if (!res.available) {
        toast.error(res.reason || 'Could not scan the Entity Docs folder.');
      } else {
        toast.success(`Scanned ${res.scanned} file${res.scanned === 1 ? '' : 's'} — ${res.inserted} new${res.truncated ? ' (folder is larger than one scan can cover — run it again)' : ''}`);
        reload();
      }
    } catch (e) { toast.error(e.message); }
    finally { setScanning(false); }
  };

  if (loading) return <Spinner />;
  if (error)   return <ErrorMsg msg={error} onRetry={reload} />;

  const docTypes = data?.docTypes || [];
  const typeOptions = [{ value: '', label: 'Select a type…' }, ...docTypes.map(t => ({ value: t.code, label: t.label }))];
  // Every name an entity goes by is in its picker label, so searching a former
  // name finds it.
  const entityItems = (entities || []).map(e => {
    const others = splitAliasNames(e.aliases);
    const name = e.name || e.entityCode;
    return { value: e.sageEntityID, label: others.length ? `${name} (also ${others.join(' · ')})` : name };
  });
  const entityChanged = (entity, suggestionsMoved) => {
    onEntityChanged?.(entity);
    if (suggestionsMoved) reload();
  };

  const documents = (data?.documents || [])
    .filter(d => !removed.has(d.documentID))
    .map(d => refiled[d.documentID] || d);
  const unfiled = documents.filter(d => d.status === 'unfiled');
  const filed = documents.filter(d => d.status === 'filed');

  const onFiled = (saved) => setRefiled(r => ({ ...r, [saved.documentID]: saved }));
  const onRemoved = (id) => setRemoved(r => new Set(r).add(id));

  const byEntity = new Map();
  for (const d of filed) {
    const key = d.sageEntityID || 'unknown';
    if (!byEntity.has(key)) byEntity.set(key, { name: d.entityName || 'Unknown entity', docs: [] });
    byEntity.get(key).docs.push(d);
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginBottom: 12 }}>
        <div style={{ fontSize: 12, color: B.textLt, maxWidth: 620 }}>
          Governance documents from the &quot;Entity Docs&quot; SharePoint folder, filed against the legal entities
          above. Nothing is filed automatically — a suggestion is offered, a person confirms it.
        </div>
        <Btn size="sm" variant="secondary" onClick={scan} loading={scanning}>Scan Entity Docs folder now</Btn>
      </div>

      {unfiled.length > 0 && (
        <>
          <div style={{ fontSize: 12, fontWeight: 700, color: B.textMd, letterSpacing: 0.5, margin: '16px 0 6px' }}>
            NEEDS FILING ({unfiled.length})
          </div>
          <Card style={{ padding: 0, marginBottom: 18 }}>
            {unfiled.map(d => (
              <UnfiledRow key={d.documentID} doc={d} entities={entities} entityItems={entityItems} typeOptions={typeOptions}
                onFiled={onFiled} onRemoved={onRemoved} onEntityChanged={entityChanged} />
            ))}
          </Card>
        </>
      )}

      <div style={{ fontSize: 12, fontWeight: 700, color: B.textMd, letterSpacing: 0.5, margin: '16px 0 6px' }}>
        FILED DOCUMENTS ({filed.length})
      </div>
      {filed.length === 0 ? (
        <EmptyState title="No documents filed yet"
          message={unfiled.length > 0 ? 'File one of the documents above to get started.' : 'Scan the Entity Docs folder to find documents to file.'} />
      ) : (
        [...byEntity.entries()].map(([key, group]) => (
          <Card key={key} style={{ padding: 0, marginBottom: 12 }}>
            <div style={{ padding: '8px 14px', background: B.surfaceMuted, fontWeight: 600, fontSize: 12, color: B.text, borderBottom: `1px solid ${B.border}` }}>
              {group.name}
            </div>
            {group.docs.map(d => (
              <FiledRow key={d.documentID} doc={d} onRemoved={onRemoved} onOwnershipUpdated={onFiled} />
            ))}
          </Card>
        ))
      )}
    </div>
  );
};
